import React, { useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import Slider from "react-slick";

import analytics from "web/script/analytics/analytics";
import browser from "web/script/utils/browser";

import LeadLink from "../lead-link/lead-link";
import MoreProducts from "./product-gallery-more-products/product-gallery-more-products";
import MoreDetails from "./product-gallery-more-details/product-gallery-more-details";
import Hint from "../hint/hint";
import Image from "./product-gallery-image/product-gallery-image";
import styles from "./product-gallery.css";
import "./slick.less";
import "./slick-theme.less";

const longPressDuration = 500;
let longPressStartTime = 0;
let firstClientX;

function ProductGallery({
    images,
    retailer,
    isZoom,
    relatedProducts,
    showGalleryCta,
    showRelatedProducts,
    tapToLead,
    tapHint
}) {
    const viewport = browser.getViewport() === browser.VIEWPORTS.DESKTOP ? "desktop" : "mobile";
    const analyticsAction = `${viewport}_gallery`;
    let lastImageIndex = images.length - 1;
    lastImageIndex = showGalleryCta ? lastImageIndex + 1 : lastImageIndex;
    lastImageIndex = showRelatedProducts ? lastImageIndex + 1 : lastImageIndex;

    const [firstInteraction, setFirstInteraction] = useState(false);
    const [seenAll, setSeenAll] = useState(false);
    const [zoomedOnce, setZoomedOnce] = useState(false);
    const [CTASeen, setCTASeen] = useState(false);
    const [relatedSeen, setRelatedSeen] = useState(false);
    const [imagesSeen, setImagesSeen] = useState(0);

    // State to check if touch is long press or swipe
    const [hasTouchMoved, setHasTouchMoved] = useState(false);

    const sliderRef = useRef(null);

    const settings = {
        speed: 200,
        cssEase: "ease-out",
        slidesToShow: 1,
        infinite: true,
        arrows: false,
        dots: true,
        lazyLoad: false,
        centerMode: false,
        touchThreshold: 20,
        // Workaround for click bug
        // https://github.com/akiran/react-slick/issues/604#issuecomment-429166338
        onSwipe: handleOnSwipe,
        swipeToSlide: true
    };

    let galleryElements = [];

    if (!isZoom) {
        settings.slidesToShow = 1.3;
        settings.variableWidth = true;
        settings.swipeToSlide = false;
    }

    galleryElements = images.map((image, index) => {
        const imageComponent = <Image isZoom={isZoom} image={image} />;
        if (tapToLead) {
            return (
                /* hack to get slick-slider ssr to set slick-track width to NaN (ignored by the
                 * browser) instead of 0, which causes the slides to start in the wrong place until
                 * the client version is initialized*/
                <div key={`image-${index}`} style={{}}>
                    <LeadLink
                        reason="image-gallery-tap"
                        href={retailer.link}
                        openInNewTab={true}
                    >
                        {imageComponent}
                    </LeadLink>
                </div>
            );
        }
        return (
            /* hack. see explanation next to he tapToLead version */
            <div key={`image-${index}`} style={{}}>
                {imageComponent}
            </div>
        );
    });

    if (showGalleryCta) {
        galleryElements.push(<MoreDetails key="retailer-slide" retailer={retailer} />);
    }

    if (showRelatedProducts) {
        galleryElements.push(<MoreProducts key="related-slide" products={relatedProducts} />);
    }

    let handleTouchMoved = useCallback(
        event => {
            if (!zoomedOnce) {
                setHasTouchMoved(true);
            }

            if (event.touches.length > 0) {
                const minValue = settings.touchThreshold;
                const clientX = event.touches[0].clientX - firstClientX;

                // Disable vertical scrolling when user swipes horizontally (Safari fix).
                if (Math.abs(clientX) > minValue) {
                    event.preventDefault();
                    event.returnValue = false;
                    return false;
                }
            }
        },
        [settings.touchThreshold, zoomedOnce]
    );

    const sliderElement = useCallback(
        node => {
            if (node === null) {
                return;
            }
            node.addEventListener("touchmove", handleTouchMoved, {
                passive: false
            });
            node.addEventListener("gesturestart", event => event.preventDefault());
        },
        [handleTouchMoved]
    );

    const handleTouchStart = useCallback(
        event => {
            if (!zoomedOnce) {
                setHasTouchMoved(false);
            }

            if (event && event.touches.length > 0) {
                firstClientX = event.touches[0].clientX;

                if (isZoom && !zoomedOnce) {
                    longPressStartTime = event.timeStamp;
                }
            }
        },
        [isZoom, zoomedOnce]
    );

    const handleTouchEnd = useCallback(
        event => {
            if (!isZoom || zoomedOnce || hasTouchMoved || !event) {
                return;
            }
            longPressStartTime = event.timeStamp - longPressStartTime;

            if (longPressStartTime >= longPressDuration) {
                setZoomedOnce(true);
                analytics.event("engagement", analyticsAction, "zoomed_in_gallery");
                if (!firstInteraction) {
                    setFirstInteraction(true);
                    analytics.event("engagement", analyticsAction, "interacted");
                }
            }
        },
        [analyticsAction, firstInteraction, hasTouchMoved, isZoom, zoomedOnce]
    );

    function onSlideChange(currentSlide, nextSlide) {
        analytics.event("engagement", analyticsAction, "changed_image");

        if (!firstInteraction) {
            setFirstInteraction(true);
            analytics.event("engagement", analyticsAction, "interacted");
        }

        if (!seenAll) {
            let tempImagesSeen = imagesSeen;
            if (nextSlide === currentSlide + 1) {
                // User scrolls right
                tempImagesSeen += 1;
            } else if (nextSlide === currentSlide - 1 || nextSlide === lastImageIndex) {
                // User scrolls left
                tempImagesSeen -= 1;
            }

            if (Math.abs(tempImagesSeen) === lastImageIndex) {
                setSeenAll(true);
                analytics.event("engagement", analyticsAction, "seen_all");
            } else {
                setImagesSeen(tempImagesSeen);
            }
        }

        if (
            (!showRelatedProducts && showGalleryCta && !CTASeen && nextSlide === lastImageIndex) ||
            (showRelatedProducts && showGalleryCta && !CTASeen && nextSlide === lastImageIndex - 1)
        ) {
            setCTASeen(true);
            analytics.event("engagement", analyticsAction, "seen_cta");
        }

        if (showRelatedProducts && !relatedSeen && nextSlide === lastImageIndex) {
            setRelatedSeen(true);
            analytics.event("engagement", analyticsAction, "seen_related_products_slide");
        }
    }

    function handleOnSwipe() {
        if (sliderRef) {
            sliderRef.current.innerSlider.clickable = true;
        }
    }

    return (
        <div
            ref={sliderElement}
            className={styles.carousel}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            {tapToLead && tapHint ? (
                <LeadLink reason="image-gallery-tap" href={retailer.link} openInNewTab={true}>
                    <Hint />
                </LeadLink>
            ) : null}
            <Slider
                ref={sliderRef}
                className={styles.carouselImage}
                beforeChange={onSlideChange}
                {...settings}
            >
                {galleryElements}
            </Slider>
        </div>
    );
}

export default ProductGallery;

const retailerShape = PropTypes.shape({
    image: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired
});

ProductGallery.propTypes = {
    images: PropTypes.array.isRequired,
    retailer: retailerShape,
    relatedProducts: PropTypes.array,
    isZoom: PropTypes.bool,
    showGalleryCta: PropTypes.bool,
    showRelatedProducts: PropTypes.bool,
    tapToLead: PropTypes.bool,
    tapHint: PropTypes.bool
};

ProductGallery.defaultProps = {
    showGalleryCta: false,
    isZoom: false,
    showRelatedProducts: false,
    tapToLead: false,
    tapHint: false
};
